import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Check, X, Plus, Minus } from 'lucide-react';

interface Guest {
  name: string;
  attending: boolean | null;
  attends_preboda: boolean | null;
}

interface FormData {
  group_size: number;
  guests: Guest[];
  message: string;
}

interface Invitado {
  id: string;
  Invitados: string;
}

const RSVPForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<FormData>({
    group_size: 1,
    guests: [{
      name: '',
      attending: null,
      attends_preboda: null
    }],
    message: ''
  });
  const [invitados, setInvitados] = useState<Invitado[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    const loadInvitados = async () => {
      const { data, error } = await supabase
        .from('guests')
        .select('*')
        .order('Invitados', { ascending: true });

      if (error) {
        console.error('Error al cargar invitados:', error);
        return;
      }

      setInvitados(data || []);
    };

    loadInvitados();
  }, []);

  const updateGroupSize = (newSize: number) => {
    if (newSize < 1 || newSize > 8) return;
    
    setFormData(prev => {
      const currentGuests = [...prev.guests];
      
      if (newSize > prev.group_size) {
        while (currentGuests.length < newSize) {
          currentGuests.push({
            name: '',
            attending: null,
            attends_preboda: null
          });
        }
      } else {
        currentGuests.splice(newSize);
      }

      return {
        ...prev,
        group_size: newSize,
        guests: currentGuests
      };
    });
  };

  const updateGuest = (index: number, field: keyof Guest, value: any) => {
    setFormData(prev => ({
      ...prev,
      guests: prev.guests.map((guest, i) => 
        i === index ? { ...guest, [field]: value } : guest
      )
    }));
  };

  const validateForm = () => {
    const hasEmptyNames = formData.guests.some(guest => !guest.name.trim());
    if (hasEmptyNames) {
      setErrorMessage('Por favor, introduce el nombre de todos los invitados.');
      return false;
    }

    const hasMissingAttendance = formData.guests.some(
      guest => guest.attending === null || guest.attends_preboda === null
    );
    if (hasMissingAttendance) {
      setErrorMessage('Por favor, selecciona si cada invitado asistirá a cada evento.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    if (!validateForm()) {
      setStatus('error');
      return;
    }

    try {
      const hasInvalidNames = formData.guests.some(
        guest => !guest.name.trim() || guest.name.length > 100
      );
      if (hasInvalidNames) {
        throw new Error('Nombre inválido');
      }

      if (formData.message && formData.message.length > 500) {
        throw new Error('Mensaje demasiado largo');
      }

      const submissions = formData.guests.map(guest => ({
        guest_name: guest.name.trim(),
        attends_boda: guest.attending === true,
        attends_preboda: guest.attends_preboda === true,
        comments: formData.message.trim() || null
      }));

      for (const submission of submissions) {
        const { data: existingGuest, error: checkError } = await supabase
          .from('wedding-guests')
          .select('*')
          .eq('guest_name', submission.guest_name)
          .single();

        if (checkError && checkError.code !== 'PGRST116') {
          throw new Error(`Error al verificar invitado: ${checkError.message}`);
        }

        if (existingGuest) {
          const { error: updateError } = await supabase
            .from('wedding-guests')
            .update({
              attends_boda: submission.attends_boda,
              attends_preboda: submission.attends_preboda,
              comments: submission.comments
            })
            .eq('id', existingGuest.id);

          if (updateError) {
            throw new Error(`Error al actualizar invitado: ${updateError.message}`);
          }
        } else {
          const { error: insertError } = await supabase
            .from('wedding-guests')
            .insert([submission]);

          if (insertError) {
            throw new Error(`Error al insertar invitado: ${insertError.message}`);
          }
        }
      }

      setFormData({
        group_size: 1,
        guests: [{
          name: '',
          attending: null,
          attends_preboda: null
        }],
        message: ''
      });

      setShowSuccessModal(true);
      setStatus('idle');

    } catch (err) {
      console.error('Error completo al enviar formulario:', err);
      setErrorMessage(err instanceof Error ? err.message : 'Error al enviar el formulario');
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : 'Error al enviar el formulario',
        variant: "destructive",
        duration: 5000,
      });
      setStatus('error');
    }
  };

  return (
    <>
      {showSuccessModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => window.location.href = '/'}
        >
          <div 
            className="bg-white/95 p-8 rounded-lg border border-gray-200 shadow-lg max-w-2xl mx-auto text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6">
              <div className="w-16 h-16 bg-wedding-burgundy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-wedding-burgundy" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">¡Confirmación recibida!</h3>
              <p className="text-lg text-gray-600">Gracias por confirmar tu asistencia. Te esperamos en nuestra celebración.</p>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 bg-wedding-burgundy text-white rounded-lg hover:bg-wedding-burgundy/90 transition-colors duration-200"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8 bg-white/95 p-8 rounded-lg shadow-lg max-w-2xl mx-auto">
        {status === 'error' && (
          <div className="bg-red-50 text-red-800 p-4 rounded-lg border border-red-200">
            {errorMessage}
          </div>
        )}

        <div className="space-y-4">
          <label className="block text-lg font-medium text-gray-900">
            Número de asistentes
          </label>
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={() => updateGroupSize(formData.group_size - 1)}
              disabled={formData.group_size <= 1}
              className="p-2 rounded-lg border-2 border-gray-300 hover:border-wedding-burgundy disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Minus className="w-5 h-5" />
            </button>
            <span className="text-xl font-medium">{formData.group_size}</span>
            <button
              type="button"
              onClick={() => updateGroupSize(formData.group_size + 1)}
              disabled={formData.group_size >= 8}
              className="p-2 rounded-lg border-2 border-gray-300 hover:border-wedding-burgundy disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        {formData.guests.map((guest, index) => (
          <div key={`guest-${index}`} className="space-y-6 p-6 border border-gray-200 rounded-lg">
            <h3 className="text-xl font-medium text-gray-900">
              Invitado {index + 1}
            </h3>

            <div className="space-y-2">
              <label htmlFor={`name_${index}`} className="block text-lg font-medium text-gray-900">
                Nombre completo *
              </label>
              <div className="relative">
                <input
                  type="text"
                  id={`name_${index}`}
                  value={guest.name}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    updateGuest(index, 'name', inputValue);
                  }}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wedding-burgundy focus:border-wedding-burgundy bg-white text-gray-900 text-lg"
                  placeholder="Escribe o selecciona un invitado"
                />
                {guest.name && guest.name.length >= 5 && invitados.filter(invitado => invitado.Invitados.toLowerCase().includes(guest.name.toLowerCase())).length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white/95 backdrop-blur-sm border border-gray-200/50 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {invitados
                      .filter(invitado => 
                        invitado.Invitados.toLowerCase().includes(guest.name.toLowerCase())
                      )
                      .map((invitado) => (
                        <div
                          key={`invitado-${invitado.id}`}
                          className="px-4 py-2.5 hover:bg-wedding-burgundy/5 cursor-pointer text-gray-900 transition-colors duration-200"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            updateGuest(index, 'name', invitado.Invitados);
                          }}
                        >
                          {invitado.Invitados}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">¿Asistirá a la preboda?</h4>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => updateGuest(index, 'attends_preboda', true)}
                  className={`flex-1 py-3 px-6 rounded-lg border-2 transition-all duration-200 flex items-center justify-center space-x-2
                    ${guest.attends_preboda === true
                      ? 'bg-wedding-burgundy text-white border-wedding-burgundy' 
                      : 'bg-white text-gray-700 border-gray-300 hover:border-wedding-burgundy hover:bg-gray-50'}`}
                >
                  <Check className="w-5 h-5" />
                  <span>Sí asistirá</span>
                </button>
                <button
                  type="button"
                  onClick={() => updateGuest(index, 'attends_preboda', false)}
                  className={`flex-1 py-3 px-6 rounded-lg border-2 transition-all duration-200 flex items-center justify-center space-x-2
                    ${guest.attends_preboda === false
                      ? 'bg-wedding-burgundy text-white border-wedding-burgundy' 
                      : 'bg-white text-gray-700 border-gray-300 hover:border-wedding-burgundy hover:bg-gray-50'}`}
                >
                  <X className="w-5 h-5" />
                  <span>No asistirá</span>
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">¿Asistirá a la boda?</h4>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => updateGuest(index, 'attending', true)}
                  className={`flex-1 py-3 px-6 rounded-lg border-2 transition-all duration-200 flex items-center justify-center space-x-2
                    ${guest.attending === true
                      ? 'bg-wedding-burgundy text-white border-wedding-burgundy' 
                      : 'bg-white text-gray-700 border-gray-300 hover:border-wedding-burgundy hover:bg-gray-50'}`}
                >
                  <Check className="w-5 h-5" />
                  <span>Sí asistirá</span>
                </button>
                <button
                  type="button"
                  onClick={() => updateGuest(index, 'attending', false)}
                  className={`flex-1 py-3 px-6 rounded-lg border-2 transition-all duration-200 flex items-center justify-center space-x-2
                    ${guest.attending === false
                      ? 'bg-wedding-burgundy text-white border-wedding-burgundy' 
                      : 'bg-white text-gray-700 border-gray-300 hover:border-wedding-burgundy hover:bg-gray-50'}`}
                >
                  <X className="w-5 h-5" />
                  <span>No asistirá</span>
                </button>
              </div>
            </div>
          </div>
        ))}

        <div className="space-y-2">
          <label htmlFor="message" className="block text-lg font-medium text-gray-900">
            Comentarios adicionales
          </label>
          <textarea
            id="message"
            value={formData.message}
            onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wedding-burgundy focus:border-wedding-burgundy bg-white text-gray-900 text-lg"
            placeholder="¿Algo más que quieras decirnos?"
          />
        </div>

        <button
          type="submit"
          disabled={status === 'loading'}
          className={`w-full py-4 px-6 rounded-lg text-lg font-medium text-white transition-all duration-200
            ${status === 'loading' 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-wedding-burgundy hover:bg-wedding-burgundy/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wedding-burgundy shadow-lg hover:shadow-xl'}`}
        >
          {status === 'loading' ? 'Enviando...' : 'Confirmar asistencia'}
        </button>
      </form>
    </>
  );
};

export default RSVPForm;